import { Controller } from "@hotwired/stimulus";

/**
 * News feed — the audience picker of a post. Each block is one rule: a target
 * of the school and the publics ticked for it. Blocks are sent as they are
 * (never multiplied into « classes × publics ») and the summary under the
 * picker is recomputed by the server at every change.
 */
export default class extends Controller {
  static targets = ["rules", "summary", "extra", "students"];
  static values = { options: Object, rules: Array, allowed: Object, labels: Object, previewUrl: String, searchUrl: String };

  connect() {
    this.state = this.rulesValue.map((rule) => ({ ...rule, audiences: [...(rule.audiences || [])] }));
    this.render();
    this.preview();
  }

  disconnect() {
    clearTimeout(this.timer);
  }

  shortcut(event) {
    this.state = [{ target_type: "school", target_id: null, audiences: JSON.parse(event.currentTarget.dataset.audiences) }];
    this.render();
    this.refresh();
  }

  add() {
    this.state.push({ target_type: "classroom", target_id: null, audiences: ["parents"] });
    this.render();
    this.refresh();
  }

  // ── Rendering ────────────────────────────────────────────────

  render() {
    this.rulesTarget.replaceChildren(...this.state.map((rule, index) => this.block(rule, index)));
    window.g4sRenderIcons?.();
  }

  block(rule, index) {
    const labels = this.labelsValue;
    const row = document.createElement("div");
    row.className = "ds-newsrule";

    const type = this.select(Object.entries(labels.targets), rule.target_type, labels.target);
    type.name = `rules[${index}][target_type]`;
    type.addEventListener("change", () => {
      rule.target_type = type.value;
      rule.target_id = null;
      rule.target_name = null;
      rule.audiences = rule.audiences.filter((audience) => this.allowedValue[rule.target_type].includes(audience));
      if (!rule.audiences.length) rule.audiences = [this.allowedValue[rule.target_type][0]];
      this.render();
      this.refresh();
    });
    row.appendChild(type);
    row.appendChild(this.targetField(rule, index));

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "ds-newsfile__remove";
    remove.setAttribute("aria-label", labels.remove);
    remove.innerHTML = '<i data-lucide="x"></i>';
    remove.addEventListener("click", () => { this.state.splice(index, 1); this.render(); this.refresh(); });
    row.appendChild(remove);

    const publics = document.createElement("div");
    publics.className = "ds-newsrule__publics";
    publics.setAttribute("role", "group");
    publics.setAttribute("aria-label", labels.recipients);
    this.allowedValue[rule.target_type].forEach((audience) => {
      const label = document.createElement("label");
      label.className = "ds-newsrule__public";
      const box = document.createElement("input");
      box.type = "checkbox";
      box.name = `rules[${index}][audiences][]`;
      box.value = audience;
      box.checked = rule.audiences.includes(audience);
      box.addEventListener("change", () => {
        rule.audiences = box.checked ? [...new Set([...rule.audiences, audience])] : rule.audiences.filter((a) => a !== audience);
        this.refresh();
      });
      label.append(box, document.createTextNode(labels.audiences[audience]));
      publics.appendChild(label);
    });
    row.appendChild(publics);
    return row;
  }

  targetField(rule, index) {
    const labels = this.labelsValue;
    const wrap = document.createElement("div");
    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = `rules[${index}][target_id]`;
    hidden.value = rule.target_id || "";
    wrap.appendChild(hidden);
    if (rule.target_type === "school") return wrap;

    if (rule.target_type === "student") {
      const search = document.createElement("input");
      search.type = "search";
      search.className = "ds-input";
      search.placeholder = labels.search_student;
      search.setAttribute("aria-label", labels.search_student);
      search.value = rule.target_name || "";
      const results = document.createElement("ul");
      results.className = "ds-newsrule__results";
      results.hidden = true;
      search.addEventListener("input", () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.search(search.value, results, rule, search, hidden), 250);
      });
      wrap.append(search, results);
      return wrap;
    }

    const options = (this.optionsValue[rule.target_type] || []).map((option) => [String(option.id), option.name]);
    const select = this.select(options, String(rule.target_id || ""), labels.choose, true);
    select.addEventListener("change", () => { rule.target_id = select.value || null; hidden.value = select.value; this.refresh(); });
    wrap.appendChild(select);
    return wrap;
  }

  select(entries, current, label, withBlank = false) {
    const select = document.createElement("select");
    select.className = "ds-input";
    select.setAttribute("aria-label", label);
    if (withBlank) select.appendChild(new Option(label, ""));
    entries.forEach(([value, text]) => select.appendChild(new Option(text, value, false, String(value) === String(current))));
    return select;
  }

  async search(query, results, rule, input, hidden) {
    if (query.trim().length < 2) { results.hidden = true; return; }
    const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, { headers: { Accept: "application/json" }, credentials: "same-origin" });
    const students = response.ok ? await response.json() : [];
    results.replaceChildren();
    if (!students.length) {
      const empty = document.createElement("li");
      empty.className = "ds-text-meta";
      empty.textContent = this.labelsValue.no_student;
      results.appendChild(empty);
    }
    students.forEach((student) => {
      const item = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = student.name;
      button.addEventListener("click", () => {
        rule.target_id = student.id;
        rule.target_name = student.name;
        input.value = student.name;
        hidden.value = student.id;
        results.hidden = true;
        this.refresh();
      });
      item.appendChild(button);
      results.appendChild(item);
    });
    results.hidden = false;
  }

  // ── Summary, computed by the server ──────────────────────────

  // The page that holds the picker saves itself: it is told of every change.
  refresh() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.preview(), 300);
    this.dispatch("changed");
  }

  // Written for the complex: the schools ticked above, which the count adds up.
  schoolsChosen() {
    const boxes = [...this.element.querySelectorAll('input[type="checkbox"][name="complex_school_ids[]"]')];
    return boxes.length ? { complex_school_ids: boxes.filter((box) => box.checked).map((box) => box.value) } : {};
  }

  async preview() {
    const rules = this.state.filter((rule) => rule.audiences.length && (rule.target_type === "school" || rule.target_id));
    if (!rules.length) {
      this.summaryTarget.textContent = this.labelsValue.empty;
      this.extraTarget.textContent = "";
      return;
    }
    this.summaryTarget.textContent = this.labelsValue.computing;
    const response = await fetch(this.previewUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      body: JSON.stringify({ rules: rules.map(({ target_type, target_id, audiences }) => ({ target_type, target_id, audiences })), visible_to_students: this.hasStudentsTarget ? this.studentsTarget.checked : true, ...this.schoolsChosen() }),
      credentials: "same-origin",
    });
    const data = await response.json();
    if (!response.ok) {
      this.summaryTarget.textContent = data.error || this.labelsValue.empty;
      this.extraTarget.textContent = "";
      return;
    }
    this.summaryTarget.textContent = data.summary;
    this.extraTarget.textContent = [data.without_account_label, data.without_parent_label].filter(Boolean).join(" · ");
  }
}
